import { cn } from '@/lib/utils'
import { formatAuditLogType, getAuditLogTypeCategory } from './utils'
import { AuditLogType } from '@/services/inference.gen'

type AuditLogBadgeProps = {
  type: string
}

// Map audit log type categories to Badge variants (for fallback)
const categoryVariants: Record<string, string> = {
  server: 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary',
  storage:
    'bg-accent/80 text-accent-foreground dark:bg-accent/90 dark:text-accent-foreground',
  user: 'bg-secondary text-secondary-foreground dark:bg-secondary/80 dark:text-secondary-foreground',
  org: 'bg-muted text-muted-foreground dark:bg-muted/70 dark:text-muted-foreground',
}

// Map specific action types to badge variants using semantic tokens
const actionVariants: Record<string, string> = {
  // Server actions
  [AuditLogType.server_create]:
    'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary border border-primary/20',
  [AuditLogType.server_delete]:
    'bg-destructive/10 text-destructive dark:bg-destructive/20 dark:text-destructive border border-destructive/20',

  // Storage actions - mirror server actions styling
  [AuditLogType.storage_create]:
    'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary border border-primary/20',
  [AuditLogType.storage_delete]:
    'bg-destructive/10 text-destructive dark:bg-destructive/20 dark:text-destructive border border-destructive/20',

  // User actions
  [AuditLogType.user_delete]:
    'bg-destructive/10 text-destructive/80 dark:bg-destructive/20 dark:text-destructive border border-destructive/20',
  [AuditLogType.user_role_update]:
    'bg-secondary/80 text-secondary-foreground dark:bg-secondary/70 dark:text-secondary-foreground border border-secondary/30',
  [AuditLogType.user_invite]:
    'bg-secondary/60 text-primary/90 dark:bg-secondary/50 dark:text-primary/90 border border-primary/20',
  [AuditLogType.user_accept_invite]:
    'bg-primary/5 text-primary/90 dark:bg-primary/10 dark:text-primary/90 border border-primary/20',

  // Org actions
  [AuditLogType.create_org_spending_limit]:
    'bg-muted/70 text-muted-foreground dark:bg-muted/40 dark:text-muted-foreground border border-muted/50',
  [AuditLogType.create_org_user_spending_limit]:
    'bg-muted/60 text-muted-foreground dark:bg-muted/30 dark:text-muted-foreground border border-muted/40',
  [AuditLogType.update_org_limit]:
    'bg-muted/50 text-foreground/80 dark:bg-muted/20 dark:text-foreground/70 border border-muted/30',
  [AuditLogType.update_org_user_limit]:
    'bg-muted/40 text-foreground/80 dark:bg-muted/10 dark:text-foreground/70 border border-muted/20',
}

export const AuditLogBadge = ({ type }: AuditLogBadgeProps) => {
  const formattedType = formatAuditLogType(type)
  const category = getAuditLogTypeCategory(type)

  // Use specific action variant if available, otherwise fall back to category variant
  const variant = actionVariants[type] || categoryVariants[category]

  return (
    <span className={cn('px-2 py-1 rounded-md text-xs font-medium', variant)}>
      {formattedType}
    </span>
  )
}
